import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Send, Trash2, Shield, Wrench, Wifi, ShieldCheck, Users, ArrowLeft, Crown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HelperSpacePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [isHelper, setIsHelper] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollingRef = useRef(null);

  // Check access
  useEffect(() => {
    if (!isAuthenticated || !token) { setLoading(false); return; }
    const checkAccess = async () => {
      try {
        const [helperRes, adminRes] = await Promise.all([
          fetch(`${API_URL}/api/helper-space/status`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/admin/check`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (helperRes.ok) {
          const d = await helperRes.json();
          setIsHelper(d.is_helper);
        }
        if (adminRes.ok) {
          const d = await adminRes.json();
          setIsAdmin(d.is_admin);
        }
      } catch (e) {}
      finally { setLoading(false); }
    };
    checkAccess();
  }, [isAuthenticated, token]);

  const fetchMessages = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/helper-space/messages`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setMessages(await res.json());
    } catch (e) {}
  }, [token]);

  useEffect(() => {
    if (!isHelper && !isAdmin) return;
    fetchMessages();
    pollingRef.current = setInterval(fetchMessages, 3000);
    return () => clearInterval(pollingRef.current);
  }, [isHelper, isAdmin, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/helper-space/messages`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: input.trim() })
      });
      if (res.ok) {
        setInput('');
        fetchMessages();
        inputRef.current?.focus();
      }
    } catch (e) {}
    finally { setSending(false); }
  };

  const deleteMessage = async (msgId) => {
    try {
      await fetch(`${API_URL}/api/helper-space/messages/${msgId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages();
    } catch (e) {}
  };

  const getAvatar = (msg) => msg.avatar
    ? `https://cdn.discordapp.com/avatars/${msg.discord_id}/${msg.avatar}.png`
    : null;

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
    if (d.toDateString() === yesterday.toDateString()) return "Hier";
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = formatDate(msg.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!isAuthenticated) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center">
        <p className="text-gray-400 mb-4">Connecte-toi avec Discord pour accéder à cet espace.</p>
        <button onClick={() => window.location.href = `${API_URL}/api/auth/discord`}
          className="px-6 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded-lg transition-colors">
          Se connecter avec Discord
        </button>
      </div>
    </div>
  );

  if (!isHelper && !isAdmin) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-yellow-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Accès restreint</h2>
        <p className="text-gray-400 mb-6">Cet espace est réservé aux helpers acceptés. Postule via la page recrutement !</p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => navigate('/recrutement')}
            className="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors text-sm">
            Postuler
          </button>
          <button onClick={() => navigate(-1)}
            className="px-5 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-gray-300 rounded-lg transition-colors text-sm">
            Retour
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col" data-testid="helper-space-page">
      {/* Header */}
      <div className="border-b border-cyan-900/30 bg-zinc-950/80 backdrop-blur-sm px-4 md:px-6 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white transition-colors p-1">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 bg-cyan-500/20 rounded-xl flex items-center justify-center">
          <Wrench className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h1 className="text-white font-bold text-sm">Espace Helper</h1>
          <p className="text-gray-500 text-xs">Canal privé — Helpers & Staff</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          <span className="text-gray-400 text-xs">{messages.length} messages</span>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-1 custom-scrollbar" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-16 h-16 bg-cyan-500/10 rounded-2xl flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-cyan-400/50" />
                </div>
                <p className="text-gray-500 text-sm">Sois le premier à écrire ici !</p>
                <p className="text-gray-600 text-xs mt-1">Canal privé des helpers AqmOpti</p>
              </div>
            ) : (
              Object.entries(groupedMessages).map(([date, msgs]) => (
                <div key={date}>
                  {/* Date separator */}
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-zinc-800"></div>
                    <span className="text-xs text-gray-500 bg-zinc-950 px-2">{date}</span>
                    <div className="flex-1 h-px bg-zinc-800"></div>
                  </div>
                  {msgs.map((msg, idx) => {
                    const prevMsg = idx > 0 ? msgs[idx - 1] : null;
                    const isGrouped = prevMsg && prevMsg.user_id === msg.user_id &&
                      (new Date(msg.created_at) - new Date(prevMsg.created_at)) < 300000;
                    const isMine = msg.user_id === user?.id;
                    return (
                      <div key={msg.id} className={`group flex items-start gap-3 hover:bg-white/[0.02] rounded-lg px-2 py-1 transition-colors ${isGrouped ? 'mt-0.5' : 'mt-3'}`}
                        data-testid={`helper-msg-${msg.id}`}>
                        {/* Avatar */}
                        <div className={`shrink-0 ${isGrouped ? 'w-8 opacity-0' : 'w-8'}`}>
                          {!isGrouped && (
                            getAvatar(msg) ? (
                              <img src={getAvatar(msg)} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-cyan-600/30 flex items-center justify-center text-cyan-400 text-xs font-bold">
                                {msg.username.charAt(0).toUpperCase()}
                              </div>
                            )
                          )}
                        </div>
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {!isGrouped && (
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className={`text-sm font-semibold ${isMine ? 'text-cyan-400' : msg.is_admin ? 'text-yellow-400' : 'text-white'}`}>
                                {msg.username}
                              </span>
                              {msg.is_admin && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-yellow-500/15 text-yellow-400 text-[10px] rounded font-medium">
                                  <Crown className="w-2.5 h-2.5" /> Staff
                                </span>
                              )}
                              {!msg.is_admin && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-cyan-500/15 text-cyan-400 text-[10px] rounded font-medium">
                                  <Wrench className="w-2.5 h-2.5" /> Helper
                                </span>
                              )}
                              <span className="text-gray-600 text-xs">{formatTime(msg.created_at)}</span>
                            </div>
                          )}
                          <p className="text-gray-300 text-sm leading-relaxed break-words">{msg.content}</p>
                        </div>
                        {/* Delete (admin only) */}
                        {isAdmin && (
                          <button onClick={() => deleteMessage(msg.id)}
                            className="shrink-0 opacity-0 group-hover:opacity-100 p-1 text-gray-600 hover:text-red-400 transition-all"
                            title="Supprimer">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-cyan-900/30 px-4 md:px-6 py-3 bg-zinc-950/50">
            <form onSubmit={sendMessage} className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Écris un message..."
                maxLength={1000}
                className="flex-1 bg-zinc-900 border border-cyan-900/30 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
                data-testid="helper-chat-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                className="px-4 py-2.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl transition-colors flex items-center gap-2"
                data-testid="helper-chat-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        {/* Sidebar info */}
        <div className="hidden lg:flex flex-col w-56 border-l border-cyan-900/30 bg-zinc-950/50 p-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-3">Ressources Helper</p>
            <div className="space-y-2">
              <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Wrench className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="text-white text-xs font-medium">Installer Windows</span>
                </div>
                <p className="text-gray-500 text-[11px]">Clé USB bootable → Installation propre 10/11</p>
              </div>
              <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Wifi className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="text-white text-xs font-medium">Drivers Wi-Fi</span>
                </div>
                <p className="text-gray-500 text-[11px]">Device Manager → Update Driver → manufacturer</p>
              </div>
              <div className="bg-zinc-900/50 border border-cyan-900/20 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="text-white text-xs font-medium">Règles helper</span>
                </div>
                <p className="text-gray-500 text-[11px]">Toujours orienter vers le staff si doute</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelperSpacePage;
